import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
} from '@fluentui/react-components';
import { Dismiss20Regular, DismissCircle20Regular } from '@fluentui/react-icons';

import { useStyles } from './style';

export const UpdateConfirmDialog = ({
  i18n,
  open,
  setOpen,
  onSave,
  invalidRatioList,
}) => {
  const classes = useStyles();

  // Simplify the logic for generating error info.
  // Currently, error info will not be empty only for multimedia ads since the minsize limitation.
  const errorTip = useMemo(() => {
    if (invalidRatioList.length === 1) {
      return i18n.getString(
        _TL_('Your image doesn\'t meet the size requirements for <b>{{ratio}} {{previewName}}</b> ads. This means that any multimedia ads using this image won\'t serve in that ad placement'),
        {
          ratio: invalidRatioList[0].aspectRatioAsRatio,
          previewName: i18n.getString(invalidRatioList[0].previewTypeString),
        }
      );
    } else if (invalidRatioList.length === 2) {
      return i18n.getString(
        _TL_('Your image doesn\'t meet the size requirements for <b>{{ratio1}} {{previewName1}} and {{ratio2}} {{previewName2}}</b> ads. This means that any multimedia ads using this image won\'t serve in that ad placement'),
        {
          ratio1: invalidRatioList[0].aspectRatioAsRatio,
          previewName1: i18n.getString(invalidRatioList[0].previewTypeString),
          ratio2: invalidRatioList[1].aspectRatioAsRatio,
          previewName2: i18n.getString(invalidRatioList[1].previewTypeString),
        }
      );
    }

    return '';
  }, [i18n, invalidRatioList]);

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onUpdate = useCallback(() => {
    onSave();
    closeDialog();
  }, [closeDialog, onSave]);

  const DismissIcon = useMemo(() => (
    <Button appearance="subtle" icon={<Dismiss20Regular />} onClick={closeDialog} />
  ), [closeDialog]);

  const onOpenChange = useCallback((event, data) => {
    // it is the users responsibility to react accordingly to the open state change
    setOpen(data.open);
  }, [setOpen]);

  return (
    <Dialog
      modalType="alert"
      // this controls the dialog open state
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogSurface aria-describedby={undefined} className={classes.container}>
        <DialogBody className="save-image-dialog">
          <DialogTitle action={DismissIcon}>
            {i18n.getString(_TL_('Are you sure you want to update the image?'))}
          </DialogTitle>
          <DialogContent>
            <div className="dialog-body">
              <div className="update-image" />
              <div className="tip-title">
                {i18n.getString(_TL_('This image will be updated in all ads and campaigns that use it.'))}
              </div>
              <div className="tip-info">
                {i18n.getString(_TL_('This update can\'t be stopped once you start it. You can check the status of the update in your Library. Are you sure you want to make this update?'))}
              </div>
            </div>
            {
              errorTip &&
              <div className={classnames('error-tip')}>
                <div className={classnames('alert', 'alert-danger')} role="alert">
                  <div className="alert__icon">
                    <DismissCircle20Regular />
                  </div>
                  <div className="alert__content">
                    <span
                      className="alert__message"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: errorTip }}
                    />
                  </div>
                </div>
              </div>
            }
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button
                className="confirm-save-button"
                appearance="primary"
                onClick={onUpdate}
              >
                {i18n.getString(_TL_('Yes'))}
              </Button>
            </DialogTrigger>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={closeDialog}>
                {i18n.getString(_TL_('No'))}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

UpdateConfirmDialog.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  invalidRatioList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
